import React from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useSelector } from 'react-redux'
import {
  ApiOutlined,
  CheckCircleOutlined,
  RiseOutlined,
} from '@ant-design/icons'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import {
  DEFAULT_PLAN_LIMIT_BENEFITS,
  DEFAULT_PLAN_LIMIT_NOTE,
  getDefaultPlanLimitCopy,
} from 'src/constants/planLimitCopy'

const Page = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 16px 48px;
  min-height: 60vh;
`

const Card = styled.div`
  width: 100%;
  max-width: 620px;
  padding: 8px 4px 0;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`

const UpgradeButton = styled.button`
  background-color: ${({ theme }) => theme.colours.avocadoGreen};
  color: white;
  font-size: 16px;
  padding: 15px 45px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colours.avocadoGreen};
  }
`

const UpsellHero = styled.div`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f6ffed 0%, #fcfff5 100%);
  border: 1px solid #d9f7be;
  margin-bottom: 18px;
`

const HeroIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #b5d922;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`

const HeroCopy = styled.div`
  min-width: 0;
`

const HeroTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #262626;
  line-height: 1.3;
`

const HeroText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #595959;
`

const PlanRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`

const PlanPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #d9d9d9;
  color: #434343;
  font-size: 13px;
  font-weight: 600;
`

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const BenefitCard = styled.div`
  padding: 14px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
`

const BenefitTitle = styled.p`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
`

const BenefitText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #8c8c8c;
`

const UpsellNote = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #8c8c8c;
`

const XeroIntegrationUpsell = () => (
  <>
    <UpsellHero>
      <HeroIcon>
        <ApiOutlined />
      </HeroIcon>
      <HeroCopy>
        <HeroTitle>Connect Xero to your receipting</HeroTitle>
        <HeroText>
          Send reconciled purchase orders straight to your accounting software —
          no re-keying invoices at month end.
        </HeroText>
      </HeroCopy>
    </UpsellHero>

    <PlanRow>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Growth
      </PlanPill>
      <PlanPill>
        <CheckCircleOutlined style={{ color: '#52c41a' }} />
        Pro
      </PlanPill>
    </PlanRow>

    <BenefitGrid>
      <BenefitCard>
        <BenefitTitle>Send bills to Xero</BenefitTitle>
        <BenefitText>
          Push reconciled orders as bills with line items, tax, and attachments.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Supplier defaults</BenefitTitle>
        <BenefitText>
          Map Xero contacts and accounts per supplier so each send is one click.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Automatic send</BenefitTitle>
        <BenefitText>
          Turn on Auto-send to Xero under Integrations (or Goods receipting).
          After an invoice is reconciled, OMS can send the bill using each
          supplier’s mapped Xero account and contact.
        </BenefitText>
      </BenefitCard>
      <BenefitCard>
        <BenefitTitle>Accurate books</BenefitTitle>
        <BenefitText>
          Keeps spend and Gross Profit aligned with what actually hit your
          accounts.
        </BenefitText>
      </BenefitCard>
    </BenefitGrid>

    <UpsellNote>
      Goods receipting stays available on OMS Lite and Starter. Growth or Pro
      connect Xero so reconciled bills send themselves.
    </UpsellNote>
  </>
)

const DefaultPlanLimitUpsell = ({ plan }) => {
  const { title, text } = getDefaultPlanLimitCopy(plan)

  return (
    <>
      <UpsellHero>
        <HeroIcon>
          <RiseOutlined />
        </HeroIcon>
        <HeroCopy>
          <HeroTitle>{title}</HeroTitle>
          <HeroText>{text}</HeroText>
        </HeroCopy>
      </UpsellHero>

      <PlanRow>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Starter
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Growth
        </PlanPill>
        <PlanPill>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          Pro
        </PlanPill>
      </PlanRow>

      <BenefitGrid>
        {DEFAULT_PLAN_LIMIT_BENEFITS.map(benefit => (
          <BenefitCard key={benefit.title}>
            <BenefitTitle>{benefit.title}</BenefitTitle>
            <BenefitText>{benefit.text}</BenefitText>
          </BenefitCard>
        ))}
      </BenefitGrid>

      <UpsellNote>{DEFAULT_PLAN_LIMIT_NOTE}</UpsellNote>
    </>
  )
}

const VARIANT_CONFIG = {
  xeroIntegration: {
    content: <XeroIntegrationUpsell />,
  },
}

const PlanLimitScreen = ({ plan, variant }) => {
  const history = useHistory()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const variantConfig = variant ? VARIANT_CONFIG[variant] : null

  return (
    <Page>
      <Card>
        {variantConfig ? (
          variantConfig.content
        ) : (
          <DefaultPlanLimitUpsell plan={plan} />
        )}
        <Footer>
          <UpgradeButton
            onClick={() => {
              history.push(`/v/${venueId}/subscriptions/oms-plans`)
            }}
          >
            <RiseOutlined style={{ marginRight: 8 }} />
            View plans
          </UpgradeButton>
        </Footer>
        <GrowthEngineHumanDemoRow />
      </Card>
    </Page>
  )
}

export default PlanLimitScreen
